import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const InteractiveMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    const pontos = [
      { nome: 'Apodi/RN', coords: [-5.664, -37.799] },
      { nome: 'Euclides da Cunha/BA', coords: [-10.53666667, -38.99777778] },
      { nome: 'São José da Lapa/MG', coords: [-19.7015, -43.9617] },
      { nome: 'Pains/MG', coords: [-20.3703, -45.6571] },
      { nome: 'Arcos/MG', coords: [-20.2981, -45.5457] },
      { nome: 'Córrego Fundo/MG', coords: [-20.4489, -45.555] },
      { nome: 'Colombo/PR', coords: [-25.29167, -49.22417] },
      { nome: 'Ponta Grossa/PR', coords: [-25.0950, -50.1620] },
      { nome: 'Almirante Tamandaré/PR', coords: [-25.3247, -49.31] },
    ];

    // Create larger custom marker icon for better touch targets
    const customIcon = L.icon({
      iconRetinaUrl: markerIcon2x,
      iconUrl: markerIcon,  
      shadowUrl: markerShadow,
      iconSize: [38, 62], // Increased for better touch targets (minimum 24px recommended)
      iconAnchor: [19, 62], // Adjusted anchor point
      popupAnchor: [0, -62], // Adjusted popup anchor
      shadowSize: [62, 62], // Increased shadow size
      shadowAnchor: [21, 62]
    });

    const ruas = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> colaboradores',
    });

    const satelite = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        maxZoom: 19,
        attribution: 'Imagens &copy; <a href="https://www.esri.com/">Esri</a>',
      }
    );

    const map = L.map(mapRef.current, {
      center: [-14.235, -51.925],
      zoom: 5,
      layers: [ruas],
    });

    const grupo = L.featureGroup().addTo(map);

    pontos.forEach((p) => {
      L.marker(p.coords, { icon: customIcon })
        .bindPopup(`<strong>${p.nome}</strong><br>${p.coords[0].toFixed(6)}, ${p.coords[1].toFixed(6)}`)
        .addTo(grupo);
    });

    map.fitBounds(grupo.getBounds().pad(0.5)); // Increased padding for better marker spacing

    L.control.layers(
      { 'Ruas (OSM)': ruas, 'Satélite (Esri)': satelite },
      { Localidades: grupo },
      { collapsed: false }
    ).addTo(map);

    L.control.scale({ metric: true, imperial: false }).addTo(map);

    // Add custom CSS for accessible touch targets
    const style = document.createElement('style');
    style.textContent = `
      /* Improve radio and checkbox controls size for touch accessibility */
      .leaflet-control-layers input[type="radio"],
      .leaflet-control-layers input[type="checkbox"] {
        min-width: 28px !important;
        min-height: 28px !important;
        transform: scale(1.8);
        margin: 12px !important;
      }
      
      /* Increase label touch targets */
      .leaflet-control-layers label {
        padding: 12px !important;
        min-height: 48px !important;
        display: flex !important;
        align-items: center !important;
        cursor: pointer !important;
        touch-action: manipulation !important;
      }
      
      /* Ensure markers have adequate spacing and touch area */
      .leaflet-marker-icon {
        cursor: pointer !important;
        touch-action: manipulation !important;
        position: relative !important;
      }
      
      /* Add invisible padding around markers for larger touch target */
      .leaflet-marker-icon::before {
        content: '';
        position: absolute;
        top: -12px;
        left: -12px;
        right: -12px;
        bottom: -12px;
        min-width: 48px;
        min-height: 48px;
        z-index: -1;
      }
      
      /* Improve control layers spacing */
      .leaflet-control-layers {
        padding: 8px !important;
      }
      
      .leaflet-control-layers-base,
      .leaflet-control-layers-overlays {
        margin: 8px 0 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      map.remove();
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="relative">
      <div 
        ref={mapRef} 
        className="w-full h-96 rounded-lg overflow-hidden border border-border shadow-md"
        role="application"
        aria-label="Mapa interativo das localidades da Quimical no Brasil"
        tabIndex={0}
      />
      <div className="sr-only" id="map-description">
        <h4>Localidades da Quimical</h4>
        <p>Mapa interativo mostrando as seguintes localidades:</p>
        <ul>
          <li>Apodi/RN - Coordenadas: -5.664, -37.799</li>
          <li>Euclides da Cunha/BA - Coordenadas: -10.537, -38.998</li>
          <li>São José da Lapa/MG - Coordenadas: -19.702, -43.962</li>
          <li>Pains/MG - Coordenadas: -20.370, -45.657</li>
          <li>Arcos/MG - Coordenadas: -20.298, -45.546</li>
          <li>Córrego Fundo/MG - Coordenadas: -20.449, -45.555</li>
          <li>Colombo/PR - Coordenadas: -25.292, -49.224</li>
          <li>Ponta Grossa/PR - Coordenadas: -25.095, -50.162</li>
          <li>Almirante Tamandaré/PR - Coordenadas: -25.325, -49.310</li>
        </ul>
        <p>Use as setas do teclado para navegar pelo mapa quando em foco.</p>
      </div>
    </div>
  );
};

export default InteractiveMap;